import { PartnerInfos, Project } from "types";

/**
 * Merge a project's snapshot partner entry with the fresh partner data held
 * in the org-wide partners store.
 *
 * `project.infos.partners[i]` stores a snapshot of partner fields taken at
 * the time the partner was attached to the project. The source of truth is
 * the `partners` Firestore collection (loaded into `useProjectStore.partners`).
 * Without this hydration, edits to a partner (name, phone, decennales, …)
 * would not surface in the project view.
 *
 * Field policy:
 *   - **Partner-level** (refreshed from store, snapshot as fallback):
 *     `display_name`, `phone_number`, `email`, `company_name`, `company_phone`,
 *     `company_address`, `company_city`, `company_postal`, `company_email`.
 *   - **Project-scoped** (kept from snapshot, never overwritten):
 *     `id`, `role`, `company_types` — `company_types` is used as the per-project
 *     role list (the "Retirer" button removes one type from the snapshot only).
 *
 * Falls back to the unchanged snapshot when the store doesn't know the partner
 * yet (still loading) or no longer does (deleted org-wide).
 */
export function resolveProjectPartner(
  snapshot: PartnerInfos,
  storePartners: PartnerInfos[],
): PartnerInfos {
  if (!snapshot?.id) return snapshot;
  const fresh = storePartners.find((p) => p.id === snapshot.id);
  if (!fresh) return snapshot;
  return {
    ...snapshot,
    display_name: fresh.display_name ?? snapshot.display_name,
    phone_number: fresh.phone_number ?? snapshot.phone_number,
    email: fresh.email ?? snapshot.email,
    company_name: fresh.company_name ?? snapshot.company_name,
    company_phone: fresh.company_phone ?? snapshot.company_phone,
    company_address: fresh.company_address ?? snapshot.company_address,
    company_city: fresh.company_city ?? snapshot.company_city,
    company_postal: fresh.company_postal ?? snapshot.company_postal,
    company_email: fresh.company_email ?? snapshot.company_email,
  };
}

export function resolveProjectPartners(
  project: Project | undefined | null,
  storePartners: PartnerInfos[],
): PartnerInfos[] {
  const list = project?.infos?.partners || [];
  return list.map((p) => resolveProjectPartner(p, storePartners));
}

/**
 * Strip a partner down to the fields that are actually project-scoped before
 * writing it into `project.infos.partners[]`. Everything else (phone, email,
 * company info, decennales) is fetched live from the partners collection at
 * render time via `resolveProjectPartner`.
 *
 * `display_name` is kept as a last-resort fallback so the UI can still show
 * something if the partner is later deleted from the org.
 */
export function stripPartnerSnapshot(partner: PartnerInfos): PartnerInfos {
  return {
    id: partner.id,
    display_name: partner.display_name,
    company_types: partner.company_types || [],
  };
}
